import { useState, useCallback, useEffect } from 'react';
import { Measurement, RoomDimension } from '../types';

const MEASUREMENTS: Measurement[] = [
  {
    id: 1,
    type: 'length',
    question: 'Convert 5 meters to centimeters',
    value: 5,
    unit: 'm',
    targetUnit: 'cm',
    points: 10,
    hint: 'Multiply by 100'
  },
  {
    id: 2,
    type: 'area',
    question: 'Calculate the area of a room with length 4m and width 3m',
    value: 12,
    unit: 'm²',
    targetUnit: 'm²',
    points: 15,
    hint: 'Area = length × width'
  },
  {
    id: 3,
    type: 'time',
    question: 'Convert 15:45 (24-hour) to 12-hour format',
    value: 3.45,
    unit: '24h',
    targetUnit: '12h',
    points: 10,
    hint: 'Subtract 12 from hours after noon'
  },
  {
    id: 4,
    type: 'volume',
    question: 'Calculate the volume of a room with length 4m, width 3m, and height 2.5m',
    value: 30,
    unit: 'm³',
    targetUnit: 'm³',
    points: 20,
    hint: 'Volume = length × width × height'
  }
];

const TIME_LIMIT = 60; // 60 seconds per level

export const useMeasureGame = () => {
  const [currentLevel, setCurrentLevel] = useState(0);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [maxAttempts] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);

  useEffect(() => {
    if (timeLeft > 0 && !gameOver) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameOver(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft, gameOver]);

  const getCurrentMeasurement = useCallback(() => {
    return MEASUREMENTS[currentLevel];
  }, [currentLevel]);

  const checkAnswer = useCallback((answer: number) => {
    const measurement = getCurrentMeasurement();
    const isCorrect = Math.abs(answer - measurement.value) < 0.001;

    if (isCorrect) {
      const timeBonus = Math.floor(timeLeft / 10);
      const bonusPoints = (maxAttempts - attempts) * 5 + timeBonus;
      const totalPoints = measurement.points + bonusPoints;
      setScore(prev => prev + totalPoints);
      
      if (currentLevel === MEASUREMENTS.length - 1) {
        setGameOver(true);
      } else {
        setCurrentLevel(prev => prev + 1);
        setAttempts(0);
        setShowHint(false);
        setTimeLeft(TIME_LIMIT);
      }
      return true;
    } else {
      setAttempts(prev => {
        const newAttempts = prev + 1;
        if (newAttempts >= maxAttempts) {
          setGameOver(true);
        }
        return newAttempts;
      });
      return false;
    }
  }, [currentLevel, attempts, maxAttempts, timeLeft]);

  const calculateArea = useCallback((dimensions: RoomDimension) => {
    return dimensions.width * dimensions.length;
  }, []);

  const calculateVolume = useCallback((dimensions: RoomDimension) => {
    return dimensions.width * dimensions.length * (dimensions.height || 0);
  }, []);

  const convertTime = useCallback((time24: string): string => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }, []);

  const toggleHint = useCallback(() => {
    setShowHint(prev => !prev);
  }, []);

  return {
    measurement: getCurrentMeasurement(),
    score,
    attempts,
    maxAttempts,
    gameOver,
    showHint,
    timeLeft,
    checkAnswer,
    toggleHint,
    calculateArea,
    calculateVolume,
    convertTime
  };
};